import AdvancedProfile from "@/comps/advanced-profile"
import { WrappedFooter } from "@/comps/footer"
import Header from "@/comps/header"
import Meta from "@/comps/meta"
import {
	FeaturePageProps,
	getLandingPageData,
} from "@/ssr/features"
import { getUserProps, ProfileProps } from "@/ssr/profile"
import { useTranslation } from "next-i18next"
import FeaturePage from "./feature/[feature]"

import Button from "@/comps/button"
import { AITool } from "@/comps/editors/deform"
import { DashboardHeader } from "@/comps/header/dashboard-header"
import { SubscriptionPopup } from "@/comps/subscription-popup"
import { getAdExamplesSSR } from "@/ssr/ad-examples"
import {
	getCategoriesForFilter,
	getInitialFilters,
	getInitialGallery,
} from "@/ssr/gallery"
import { getTemplates } from "@/ssr/templates"
import useBranchRedirect, {
	createAppLink,
} from "@/utils/branch"
import { Device, useDeviceSize } from "@/utils/breakpoints"
import { assetUrl, HEADER_WEB_VERSION } from "@/utils/cdn"
import useAuth from "@/utils/client-auth"
import { dateToSortableString } from "@/utils/dates"
import { getErrorLog } from "@/utils/errors"
import { useIndexedDB } from "@/utils/indexed"
import { redirectToLogin } from "@/utils/login-redirect"
import { base, code } from "@/utils/queryfy"
import {
	SSRNotFound,
	SSRTemporaryRedirect,
	wrapSSRAuth,
} from "@/utils/ssr-auth"
import { useShortCut } from "@kartyom/shared-components"
import axios from "axios"
import clsx from "clsx"
import {
	CompletedMessageProps,
	createSessionsRecord,
	DashboardControllerInitialData,
	extractSessionsToGroups,
	getDateCategory,
	useDashboardController,
	WizardProps,
} from "controllers/dashboard"
import {
	DEFORUM_STORAGE_KEY,
	DynamicEditorProps,
	getStorage,
	useEditorsController,
} from "controllers/editors"
import {
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react"
import { DashboardContent } from "sections/dashboard/content"
import {
	Popups,
	PopupsProps,
} from "sections/dashboard/popups"
import { ReferenceFacePopup } from "sections/dashboard/reference-face-popup"
import { SideBar } from "sections/dashboard/side-bar"
import { SessionItemGroup } from "sections/dashboard/video-gpt"
import {
	AdExamplesPopup,
	AdExamplesPopupProps,
} from "sections/gallery/ad-examples-popup"
import { Pages } from "sections/gallery/all-gallery"
import {
	GalleryPopup,
	GalleryPopupProps,
} from "sections/gallery/gallery-popup"
import {
	HistoryPopup,
	HistoryPopupProps,
} from "sections/history/history-popup"
import { CompletedMessagePopup } from "sections/wizard/completed-message-popup"
import styled from "styled-components"
import { z } from "zod"
import { EditSegmentsPopup } from "../sections/wizard/edit-segments-popup"
import { FeedbackMessagePopup } from "../sections/wizard/feedback-message"
import { EditSegmentsAction } from "../sections/wizard/response-message"
import { indexFiltersAdExamples } from "./ad-examples"
import {
	FilterStateAdExamples,
	FilterStateGallery,
	FilterStateTemplates,
	indexFiltersGallery,
} from "./ai-gallery"
import {
	historyEntityExtractor,
	historyEntrySchema,
} from "./api/deforum-history"
import {
	chatListResponseSchema,
	chatMessageSchema,
	sessionStatuses,
} from "./api/wizard-list-chat"
import { StartSessionWithInputsRequest } from "./api/wizard-start-session-with-inputs"
import { indexFiltersTemplates } from "./video-templates"

const Container = styled.div`
	display: flex;
	flex-direction: column;

	width: var(--w-screen);
`

const UserPage = (props: ProfileProps) => {
	const { user, userGallery, activeTab } = props

	const { t } = useTranslation()
	const [link, setLink] = useState<string | null>(null)

	useEffect(() => {
		const uid = user.uid
		async function handler() {
			const deepLink = await createAppLink(uid)
			setLink(deepLink)
		}

		handler()
	}, [user.uid])

	const { picture } = user

	return (
		<Container className="min-h-screen justify-between">
			<Meta
				title={t("fs_user_profile", user)}
				description={t("fs_user_description", user)}
			/>
			<div className="flex w-full flex-1 flex-col bg-color-cell">
				<Header />
				<AdvancedProfile
					user={user}
					gallery={userGallery}
					activeTab={activeTab}
				/>

				<div
					className={clsx(
						"fixed bottom-0 left-0 z-[999] w-screen rounded-t-[12px] bg-color-cell desktop:hidden",
						"transition-all",
						"[box-shadow:0px_0px_40px_rgba(0,0,0,0.4)]",
						link
							? "translate-y-0"
							: "translate-y-[calc(100%+40px)]",
					)}>
					<div className="relative flex flex-col items-center justify-center gap-2 p-5">
						<div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
							<img
								src={
									picture || assetUrl("/general/avatar.svg")
								}
								alt="profile pic"
								className="h-[70px] w-[70px] rounded-full border-2 border-color-cell"
							/>
						</div>
						<span className="pt-5 text-[18px] font-700">
							See full profile in the app
						</span>
						<Button
							text="Open App"
							full
							primary
							href={link || "/"}
						/>
						<button
							className="text-[15px] font-600 text-primary-700"
							onClick={(e) => {
								e.preventDefault()
								setLink(null)
							}}>
							Not Now
						</button>
					</div>
				</div>
			</div>
			<WrappedFooter />
		</Container>
	)
}

const DashboardPages = (
	props: DashboardControllerInitialData,
) => {
	useBranchRedirect({ users: true, templates: true })

	const { t } = useTranslation()
	const [subscriptionPopupOpen, setSubscriptionPopupOpen] =
		useState(false)
	const { userInfo } = useAuth()

	const dashboard = useDashboardController(props)

	const editors = useEditorsController({
		tool: dashboard.home.selectedTool,
	})

	const device = useDeviceSize()

	const [isClosedSidebar, setCloseSidebar] = useState(true)
	const [historyPopupState, setHistoryPopupState] =
		useState<HistoryPopupProps | null>(null)
	const [galleryPopupState, setGalleryPopupState] =
		useState<GalleryPopupProps | null>(null)
	const [adExamplesPopupState, setAdExamplesPopupState] =
		useState<AdExamplesPopupProps | null>(null)
	const [popupsProps, setPopupsProps] =
		useState<PopupsProps | null>(null)
	const textAreaRef = useRef<HTMLDivElement>(null)
	const [changeName, setChangeName] =
		useState<SessionItemGroup>(null)
	const [deleteSessionPopup, setDeleteSessionPopup] =
		useState<null | number>(null)
	const [completedMessage, setCompletedMessage] =
		useState<CompletedMessageProps | null>(null)
	const [indexedPayload, setIndexedPayload] =
		useState<DynamicEditorProps | null>(null)
	const [openUploadStockPopup, setOpenUploadStockPopup] =
		useState<EditSegmentsAction | null>(null)

	const data = getStorage(
		dashboard.home.selectedTool ?? "deform",
	)
	const indexed = useIndexedDB(
		"ai_art",
		data.tool,
		data.storage,
	)

	const [isFirstTime, setIsFirstTime] = useState(true)
	const selectCreatorTool = useCallback(
		async (
			tool: AITool,
			content: DynamicEditorProps,
			changePage?: boolean,
		) => {
			if (changePage) {
				await dashboard.changeSelectedPage("home")
			}
			setIsFirstTime(false)
			dashboard.selectAiStudioTool(tool)
			editors.changeSelectedTool(content)
			editors.loadToolFilters(content, true)
		},
		[dashboard, editors],
	)

	const selectedTool = useRef(dashboard.home.selectedTool)

	useEffect(() => {
		if (
			dashboard.activePage === "home" &&
			selectedTool.current !== null &&
			!editors.hasNotSelectedTool &&
			isFirstTime
		) {
			if (indexed.status !== "ready") {
				return
			}

			setIsFirstTime(false)

			indexed
				.getData(DEFORUM_STORAGE_KEY)
				.then(async (data) => {
					if (!data) {
						return
					}

					selectCreatorTool(data.tool, data)

					indexed.clearData(DEFORUM_STORAGE_KEY)
				})

			switch (selectedTool.current) {
				case "deform":
					dashboard.changeActiveChild("txt_deform_ai")
					editors.loadToolFilters(
						{
							tool: "deform",
							id: "last",
							promptType: "custom",
							inputs: [],
							location: "local",
						},
						true,
					)
					break

				case "ai_restyle":
					dashboard.changeActiveChild("txt_restyle_ai")
					selectCreatorTool("ai_restyle", {
						tool: "ai_restyle",
						id: DEFORUM_STORAGE_KEY,
						promptType: "custom",
						input: "",
						location: "local",
					})
					break

				case "text_to_image":
					dashboard.changeActiveChild("txt_text_to_image")
					selectCreatorTool("text_to_image", {
						tool: "text_to_image",
						id: DEFORUM_STORAGE_KEY,
						promptType: null,
						text: "",
						location: "local",
					})
					break

				case "ai_photo":
					dashboard.changeActiveChild("txt_photo_ai")
					selectCreatorTool("ai_photo", {
						tool: "ai_photo",
						id: DEFORUM_STORAGE_KEY,
						promptType: "custom",
						input: "",
						location: "local",
					})
					break
			}
		}
	}, [
		dashboard,
		editors,
		indexed,
		isFirstTime,
		selectCreatorTool,
	])

	useEffect(() => {
		if (
			historyPopupState !== null ||
			adExamplesPopupState !== null ||
			galleryPopupState !== null
		) {
			document.body.style.overflow = "hidden"
		} else {
			document.body.style.overflow = "unset"
		}
	}, [
		adExamplesPopupState,
		galleryPopupState,
		historyPopupState,
	])

	useShortCut({
		shortCut: "BracketLeft",
		handler: () => setCloseSidebar(true),
	})

	useShortCut({
		shortCut: "BracketRight",
		handler: () => setCloseSidebar(false),
	})

	useEffect(() => {
		if (popupsProps === null) {
			document.body.style.overflow = "visible"
		} else {
			document.body.style.overflow = "hidden"
		}
	}, [popupsProps])

	useEffect(() => {
		if (device !== Device.Desktop) {
			setCloseSidebar(true)
		} else {
			setCloseSidebar(false)
		}
	}, [device])

	useEffect(() => {
		if (userInfo.isAnonymous) {
			redirectToLogin()
		}
	}, [userInfo.isAnonymous])

	async function addToStorage() {
		if (
			indexed.status !== "ready" ||
			indexedPayload === null
		) {
			return
		}

		await indexed.setData(indexedPayload)
	}

	return (
		<>
			<Meta
				title={t("txt_home_title")}
				description={t("txt_home_description")}
			/>

			<DashboardHeader
				activePage={dashboard.activePage}
				setCloseSidebar={setCloseSidebar}
				isClosed={isClosedSidebar}
				changeSelectedPage={dashboard.changeSelectedPage}
				setSubscriptionPopupOpen={setSubscriptionPopupOpen}
				lastQuery={dashboard.lastQuery}
			/>

			<SideBar
				changeActiveChild={dashboard.changeActiveChild}
				activeChild={dashboard.activeChild}
				changeSelectedPage={dashboard.changeSelectedPage}
				activePage={dashboard.activePage}
				closeSidebar={() => setCloseSidebar((o) => !o)}
				isClosed={isClosedSidebar}
				setSubscriptionPopupOpen={setSubscriptionPopupOpen}
				sessionsGroup={{
					setChangeName,
					selectSession:
						dashboard.chatController.selectSession,
					loadMoreSessions:
						dashboard.chatController.loadMoreSessions,
					setDeleteSessionPopup,
					clearChatScreen:
						dashboard.chatController.clearChatScreen,
					remakeSession:
						dashboard.chatController.remakeSession,
					duplicateSession:
						dashboard.chatController.duplicateSession,
					selectedSessionId: dashboard.chat.selectedSession,
					sessions: dashboard.chat.content,
					sessionsGroup: dashboard.chat.sessionsGroup,
					changeName,
					textAreaRef,
				}}
				selectedTool={dashboard.home.selectedTool}
				selectCreatorTool={selectCreatorTool}
			/>

			<div
				className={clsx(
					isClosedSidebar
						? "desktop:w-full"
						: "desktop:ml-[240px] desktop:w-[calc(100%-240px)]",
					"flex h-full w-full bg-color-dashboard-bg pt-[58px] tablet:pt-[70px]",
					"no-scrollbar transition-all duration-[170ms] ease-in-out",
				)}>
				<DashboardContent
					loadMoreHistoryContent={
						dashboard.loadMoreHistoryContent
					}
					changeActiveChild={dashboard.changeActiveChild}
					renameSession={
						dashboard.chatController.renameSession
					}
					generateVideo={(m: string) =>
						dashboard.chatController.startNewSession(
							{ website_url: null, initial_message: m },
							null,
						)
					}
					changeSelectedPage={dashboard.changeSelectedPage}
					loadMoreSessions={
						dashboard.chatController.loadMoreSessions
					}
					deleteSession={
						dashboard.chatController.deleteSession
					}
					canTryAgain={dashboard.canTryAgain}
					setHistoryPopupState={setHistoryPopupState}
					setGalleryPopupState={setGalleryPopupState}
					deleteHistoryItem={dashboard.deleteHistoryItem}
					setPopupsProps={setPopupsProps}
					selectCreatorTool={selectCreatorTool}
					changeDiscoverFilter={
						dashboard.changeDiscoverFilter
					}
					startNewSession={() =>
						dashboard.chatController.startNewSession(
							{
								website_url: null,
								initial_message: null,
							},
							null,
						)
					}
					changeProjectsDisplay={
						dashboard.changeProjectsDisplay
					}
					historyPopupState={historyPopupState}
					activePage={dashboard.activePage}
					duplicateSession={
						dashboard.chatController.duplicateSession
					}
					remakeSession={
						dashboard.chatController.remakeSession
					}
					setStartSessionInputs={(
						message: StartSessionWithInputsRequest,
					) =>
						dashboard.chatController.startNewSession(
							{
								website_url: null,
								initial_message: null,
							},
							message,
						)
					}
					setSelectedSession={
						dashboard.chatController.selectSession
					}
					setSubscriptionPopupOpen={
						setSubscriptionPopupOpen
					}
					chatController={dashboard.chatController}
					setChangeName={setChangeName}
					changeName={changeName}
					textAreaRef={textAreaRef}
					setDeleteSessionPopup={setDeleteSessionPopup}
					deleteSessionPopup={deleteSessionPopup}
					setCompletedMessage={setCompletedMessage}
					editors={editors}
					setAdExamplesPopupState={setAdExamplesPopupState}
					setIndexedPayload={setIndexedPayload}
					openUploadStockPopup={setOpenUploadStockPopup}
					videoGpt={dashboard.videoGpt}
					chat={dashboard.chat}
					home={dashboard.home}
					discover={dashboard.discover}
				/>
			</div>

			<SubscriptionPopup
				isOpen={subscriptionPopupOpen}
				close={() => setSubscriptionPopupOpen(false)}
				addToStorage={addToStorage}
			/>

			{popupsProps !== null &&
				popupsProps.data !== null && (
					<Popups
						data={popupsProps.data}
						initialSelected={popupsProps.initialSelected}
						close={popupsProps.close}
						setInputs={popupsProps.setInputs}
						setSubscriptionPopupOpen={
							setSubscriptionPopupOpen
						}
					/>
				)}

			{historyPopupState !== null && (
				<HistoryPopup {...historyPopupState} />
			)}

			{galleryPopupState !== null && (
				<GalleryPopup {...galleryPopupState} />
			)}

			{adExamplesPopupState !== null && (
				<AdExamplesPopup {...adExamplesPopupState} />
			)}

			{completedMessage && (
				<CompletedMessagePopup
					initial={completedMessage}
					setCompletedMessage={setCompletedMessage}
				/>
			)}

			<ReferenceFacePopup />

			<FeedbackMessagePopup
				id={dashboard.chat.selectedSession ?? -1}
				changeShowRating={
					dashboard.chatController.changeShowRatingValue
				}
			/>

			{openUploadStockPopup !== null &&
				dashboard.chatController.lastResponse?.name ===
					"edit_segments" && (
					<EditSegmentsPopup
						initialSelected={openUploadStockPopup}
						closePopup={() => setOpenUploadStockPopup(null)}
						data={dashboard.chatController.lastResponse}
						sendSelectedMedias={
							dashboard.chatController.sendMessage
						}
					/>
				)}
		</>
	)
}

export default function UserFeatureDistributer(
	props: PageWithProps,
): JSX.Element {
	switch (props.page) {
		case "user":
			return <UserPage {...props.props} />
		case "feature":
			return <FeaturePage {...props.props} />
		case "dashboard":
			return <DashboardPages {...props.props} />
	}
}

type PageWithProps =
	| {
			page: "user"
			props: ProfileProps
	  }
	| {
			page: "feature"
			props: FeaturePageProps
	  }
	| {
			page: "dashboard"
			props: DashboardControllerInitialData
	  }

export const getInputsResponseSchema = z.object({
	aspects: z
		.object({
			id: z.string(),
			name: z.string(),
		})
		.array(),
	actors: z
		.object({
			id: z.string(),
			name: z.string(),
			thumbnail: z.string(),
			animated_thumbnail: z.string(),
			gender: z.enum(["male", "female"]).nullable(),
		})
		.array(),
	voices: z
		.object({
			id: z.string(),
			name: z.string(),
			preview_url: z.string(),
			tags: z.string().array(),
			gender: z.enum(["male", "female"]).nullable(),
		})
		.array(),
	music: z
		.object({
			id: z.string(),
			name: z.string(),
		})
		.array(),
})

async function getPopupsData(token: string) {
	try {
		const response = await axios
			.get<unknown>(
				`${base}/${code}/v2/wizard/get-inputs`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
						"web-version": HEADER_WEB_VERSION,
					},
				},
			)
			.then((res) => res.data)

		const result = getInputsResponseSchema.parse(response)
		return result
	} catch {
		throw new SSRNotFound()
	}
}

export const captionsResponseSchema = z
	.object({
		id: z.string(),
		thumb_url: z.string(),
		thumb_gif_url: z.string().nullable(),
	})
	.array()

async function getCaptions(token: string) {
	try {
		const response = await axios
			.get<unknown>(
				`${base}/${code}/v2/resources/caption-templates?offset=0&limit=40`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
						"web-version": HEADER_WEB_VERSION,
					},
				},
			)
			.then((res) => res.data)

		const result = captionsResponseSchema.parse(response)
		return result
	} catch {
		throw new SSRNotFound()
	}
}

async function fetchVideos(
	filters: AITool[],
	offset: number,
	token: string,
) {
	const responseSchema = z.object({
		result: z.array(historyEntrySchema),
	})

	const response = await axios
		.get<unknown>(`${base}/${code}/v2/deform/history`, {
			params: {
				offset,
				limit: 10,
				"filter[tools]": filters
					.map((filter) => `'${filter}'`)
					.join(","),
			},
			headers: {
				Authorization: `Bearer ${token}`,
				"web-version": HEADER_WEB_VERSION,
			},
		})
		.then((res) => res.data)

	const parsed = responseSchema.parse(response)

	return parsed.result.map(historyEntityExtractor)
}

export const sessionsListResponseSchema = z.object({
	sessions: z
		.object({
			id: z.number(),
			interacted_at: z.string(),
			name: z.string(),
			thumbnail_url: z.string().nullable(),
		})
		.array(),
})

async function getChatSessions(
	limit: number,
	offset: number,
	token: string,
) {
	const sessionsListResponseSchema = z.object({
		sessions: z
			.object({
				id: z.number(),
				interacted_at: z.string(),
				name: z.string(),
				status: sessionStatuses,
				started: z.string().optional(),
				messages: chatMessageSchema
					.array()
					.nullable()
					.default(null),
				thumbnail_url: z.string().nullable(),
			})
			.array(),
	})
	const response = await axios
		.get<unknown>(
			`${base}/${code}/v2/wizard/list-sessions`,
			{
				params: {
					limit,
					offset,
				},
				headers: {
					Authorization: `Bearer ${token}`,
					"web-version": HEADER_WEB_VERSION,
				},
			},
		)
		.then((res) => res.data)

	return sessionsListResponseSchema
		.parse(response)
		.sessions.map((session) => {
			return {
				id: session.id,
				name: session.name,
				messages: session.messages,
				status: session.status,
				started: getDateCategory(session.interacted_at),
				interacted_at: session.interacted_at,
				uploadedMedias: {
					medias: [],
					status: "idle" as const,
				},
				thumbnail_url: session.thumbnail_url,
			}
		})
}

async function getSession(id: number, token: string) {
	const response = await axios
		.get<unknown>(`${base}/${code}/v2/wizard/list-chat`, {
			params: {
				session_id: id,
			},

			headers: {
				Authorization: `Bearer ${token}`,
				"web-version": HEADER_WEB_VERSION,
			},
		})
		.then((r) => r.data)

	const res = chatListResponseSchema.parse(response)

	if (res.session_status === "error") {
		return {
			id,
			name: res.session_name ?? "New Session",
			messages: res.messages.reverse(),
			status: res.session_status,
			started: getDateCategory(res.session_interacted_at),
			interacted_at: dateToSortableString(new Date()),
			uploadedMedias: {
				medias: [],
				status: "idle" as const,
			},
			errorType: "unknown",
			thumbnail_url: null,
		}
	} else if (res.session_status === "retryable_error") {
		return {
			id,
			name: res.session_name ?? "New Session",
			messages: res.messages.reverse(),
			status: res.session_status,
			started: getDateCategory(res.session_interacted_at),
			interacted_at: dateToSortableString(new Date()),
			uploadedMedias: {
				medias: [],
				status: "idle" as const,
			},
			errorType: "retry",
			thumbnail_url: null,
		}
	} else {
		return {
			id,
			name: res.session_name ?? "New Session",
			messages: res.messages.reverse(),
			status: res.session_status,
			started: getDateCategory(res.session_interacted_at),
			interacted_at: dateToSortableString(new Date()),
			uploadedMedias: {
				medias: [],
				status: "idle" as const,
			},
			thumbnail_url: null,
			showRating:
				res.session_status === "completed" &&
				res.messages[0].name === "completed" &&
				res.messages[0].generation.show_rating,
		}
	}
}

async function getJobs(
	token: string,
	filter: Pages,
	payloadTemplates: FilterStateTemplates,
	payloadGallery: FilterStateGallery,
	payloadAdExamples: FilterStateAdExamples,
) {
	if (filter === "video-templates") {
		return await getTemplates(token, payloadTemplates)
	} else if (filter === "ai-gallery") {
		return await getInitialGallery(payloadGallery, 1)
	} else if (filter === "ad-examples") {
		return await getAdExamplesSSR(
			token,
			payloadAdExamples,
			1,
		)
	}
}

function getDiscoverPagesPayload(
	filter: Pages,
	categoryId: string | null,
	q: string | null,
) {
	let payloadGallery: FilterStateGallery =
		indexFiltersGallery
	let payloadTemplates: FilterStateTemplates =
		indexFiltersTemplates
	let payloadAdExamples: FilterStateAdExamples =
		indexFiltersAdExamples

	if (filter === "ai-gallery") {
		payloadGallery = {
			...indexFiltersGallery,
			searchQuery: q,
		}
	} else if (filter === "video-templates") {
		payloadTemplates = {
			...indexFiltersTemplates,
			selectedCategory: categoryId
				? Number(categoryId)
				: -2,
			searchQuery: q,
			applyFilters: {
				...indexFiltersTemplates.applyFilters,
				selectedCategory: categoryId
					? Number(categoryId)
					: -2,
				searchQuery: q,
			},
		}
	} else {
		payloadAdExamples = {
			...indexFiltersAdExamples,
			searchQuery: q,
			applyFilters: {
				...indexFiltersAdExamples.applyFilters,
				searchQuery: q,
			},
		}
	}

	return {
		payloadGallery,
		payloadTemplates,
		payloadAdExamples,
	}
}

export const getServerSideProps =
	wrapSSRAuth<PageWithProps>(async (context, userInfo) => {
		const username = context.params?.feature
		const tab = context.query?.active
		const discoverFilterQuery = context.query.filter
		const discoverSearchQuery = context.query.q
		const discoverCategoryQuery = context.query.categoryId
		const groupIdQuery = context.query.group_id
		const groupNameQuery = context.query.group_name
		const sessionIdQuery = context.query.session_id
		const initialMessageQuery =
			context.query.initial_message
		const selectedToolQuery = context.query.tool

		if (typeof username !== "string") {
			throw new SSRNotFound()
		}

		let filter: Pages = "ai-gallery"
		if (typeof discoverFilterQuery === "string") {
			if (discoverFilterQuery === "video-templates") {
				filter = "video-templates"
			} else if (discoverFilterQuery === "ad-examples") {
				filter = "ad-examples"
			}
		}

		let q = null
		if (typeof discoverSearchQuery === "string") {
			q = discoverSearchQuery
		}

		let categoryId = null
		if (typeof discoverCategoryQuery === "string") {
			categoryId = discoverCategoryQuery
		}

		switch (username) {
			case "chat":
				if (userInfo.isAnonymous) {
					throw new SSRTemporaryRedirect(
						`/login?redirect_url=${encodeURIComponent(
							username,
						)}`,
					)
				}
				let groupId: number | null = null
				if (typeof groupIdQuery === "string") {
					groupId = Number(groupIdQuery)
				}

				let groupName: string | null = null
				if (typeof groupNameQuery === "string") {
					groupName = groupNameQuery
				}

				let sessionId: number | null = null
				if (typeof sessionIdQuery === "string") {
					sessionId = Number(sessionIdQuery)
				}

				const [sessions] = await Promise.all([
					getChatSessions(20, 0, userInfo.accessToken),
				])

				let initialMessage: string | null = null
				if (typeof initialMessageQuery === "string") {
					initialMessage = initialMessageQuery
				}

				let group: WizardProps["group"] = null
				if (groupId !== null && groupName !== null) {
					group = {
						id: groupId,
						name: groupName,
					}
				}

				if (sessionId !== null) {
					const sessionIndex = sessions.findIndex(
						(s) => s.id === sessionId,
					)
					if (sessionIndex !== -1) {
						sessions.splice(sessionIndex, 1)
					}
					const session = await getSession(
						sessionId,
						userInfo.accessToken,
					)
					sessions.push(session)
				}

				const gpt = {
					page: "dashboard",
					props: {
						name: "chat",
						initialPage: {
							content: createSessionsRecord(sessions),
							sessionsGroup: extractSessionsToGroups(
								sessions,
								[],
							),
							selectedSession: sessionId,
							initialSessionId: sessionId,
							initialInputs: null,
							initialMessage,
							group,
						},
					},
				} as PageWithProps
				return gpt

			case "home":
				let selectedTool: AITool | null = null
				if (typeof selectedToolQuery === "string") {
					selectedTool = selectedToolQuery as AITool | null
				}
				if (userInfo.isAnonymous) {
					throw new SSRTemporaryRedirect(
						`/login?redirect_url=${encodeURIComponent(
							username,
						)}`,
					)
				}
				const historyFilter: AITool[] = [
					"deform",
					"ai_photo",
					"ai_restyle",
					"text_to_image",
					"text_to_video",
				]
				const home = {
					page: "dashboard",
					props: {
						name: "home",
						initialPage: {
							content: await fetchVideos(
								historyFilter,
								0,
								userInfo.accessToken,
							),
							filter: historyFilter,
							selectedTool,
						},
					},
				} as PageWithProps
				return home

			case "discover":
				if (userInfo.isAnonymous) {
					throw new SSRTemporaryRedirect(
						`/login?redirect_url=${encodeURIComponent(
							username,
						)}`,
					)
				}
				const {
					payloadGallery,
					payloadTemplates,
					payloadAdExamples,
				} = getDiscoverPagesPayload(filter, categoryId, q)

				const [
					jobs,
					availableFiltersGallery,
					availableFiltersTemplates,
				] = await Promise.all([
					getJobs(
						userInfo.accessToken,
						filter,
						payloadTemplates,
						payloadGallery,
						payloadAdExamples,
					),
					getInitialFilters(),
					getCategoriesForFilter(userInfo.accessToken),
				])

				const discover = {
					page: "dashboard",
					props: {
						name: "discover",
						initialPage: {
							content: {
								initialGallery:
									filter === "ai-gallery" ? jobs : null,
								initialFiltersGallery: payloadGallery,
								initialTemplates:
									filter === "video-templates"
										? jobs
										: null,
								initialFiltersTemplates: payloadTemplates,
								initialAdExamples:
									filter === "ad-examples" ? jobs : null,
								initialFiltersAdExamples: payloadAdExamples,
								availableFiltersGallery,
								availableFiltersTemplates,
							},
							filter,
							q,
							categoryId,
						},
					},
				} as PageWithProps
				return discover

			case null:
			case "video-gpt":
				if (userInfo.isAnonymous) {
					throw new SSRTemporaryRedirect(
						`/login?redirect_url=${encodeURIComponent(
							username,
						)}`,
					)
				}
				const [data, popupsData, captions] =
					await Promise.all([
						getChatSessions(20, 0, userInfo.accessToken),
						getPopupsData(userInfo.accessToken),
						getCaptions(userInfo.accessToken),
					])

				const videoGpt = {
					page: "dashboard",
					props: {
						name: "videoGpt",
						initialPage: {
							content: data,
							projectsDisplay: "column",
							text: "",
							popupsData: {
								...popupsData,
								captions,
							},
						},
					},
				} as PageWithProps

				return videoGpt

			default:
				let activeTab = 0

				if (typeof tab === "string") {
					activeTab = Number(tab)
				}

				try {
					const slug = username
					const result = await getLandingPageData(
						slug,
						null,
						"main",
					)

					const props = {
						page: "feature",
						props: result,
					} as const

					return props
				} catch {
					try {
						const user = await getUserProps(
							username,
							userInfo.accessToken,
						)

						const payload: FilterStateGallery = {
							ownedBy: user.user.uid,
							searchQuery: null,
							selectedCategories: [],
							selectedStyles: [],
							mediaType: "all",
							includeCustoms: false,
							sortBy: "best",
							selectedTools: [],
							applyFilters: null,
						}

						const [jobs, availableFilters] =
							await Promise.all([
								getInitialGallery(payload, 1),
								getInitialFilters(),
							])

						const result: ProfileProps = {
							user: user.user,
							userGallery: {
								jobs,
								initial: payload,
								availableFilters,
							},
							activeTab,
						}

						const props = {
							page: "user",
							props: result,
						} as const

						return props
					} catch (e) {
						getErrorLog(e, "/")
						throw new SSRNotFound()
					}
				}
		}
	}, [])
